import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";

const LOGOS = [
  {
    id: "primary",
    name: "Primary Logo",
    desc: "Full brand logo with text — ideal for website header, business cards, letterheads",
  },
  {
    id: "stacked",
    name: "Stacked Logo",
    desc: "Compact stacked layout — ideal for Instagram profile picture, app icon, favicon",
  },
  {
    id: "minimal",
    name: "Minimal Mark",
    desc: "Icon-only mark — ideal for watermarks, social media avatar, small spaces",
  },
  {
    id: "display",
    name: "Display Photo",
    desc: "Instagram/social media display photo — circular-crop optimized",
  },
];

function DownloadButton({ targetRef, filename, size = 2 }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!targetRef.current || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(targetRef.current, {
        scale: size,
        useCORS: true,
        backgroundColor: null,
      });
      const link = document.createElement("a");
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
    }
    setDownloading(false);
  };

  return (
    <button
      data-testid={`download-${filename}`}
      onClick={handleDownload}
      disabled={downloading}
      style={{
        background: "#d9fb06",
        color: "#1a1c1b",
        border: "none",
        padding: "10px 24px",
        fontFamily: "Inter, Arial, sans-serif",
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        cursor: downloading ? "wait" : "pointer",
        opacity: downloading ? 0.6 : 1,
      }}
    >
      {downloading ? "Generating..." : "Download PNG"}
    </button>
  );
}

function PrimaryLogo() {
  const ref = useRef(null);
  return (
    <div>
      <div
        ref={ref}
        style={{
          width: 800,
          height: 400,
          background: "#1a1c1b",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {/* Compass icon */}
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="28" stroke="#d9fb06" strokeWidth="2" />
            <circle cx="32" cy="32" r="3" fill="#d9fb06" />
            <line x1="32" y1="8" x2="32" y2="20" stroke="#d9fb06" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="32" y1="44" x2="32" y2="56" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <line x1="8" y1="32" x2="20" y2="32" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            <line x1="44" y1="32" x2="56" y2="32" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
            {/* Needle */}
            <polygon points="32,10 28,32 32,30 36,32" fill="#d9fb06" opacity="0.9" />
            <polygon points="32,54 28,32 32,34 36,32" fill="#d9fb06" opacity="0.3" />
          </svg>

          <div>
            <div
              style={{
                fontFamily: "Inter, Arial, sans-serif",
                fontWeight: 900,
                fontSize: 72,
                color: "#d9fb06",
                letterSpacing: "0.08em",
                lineHeight: 1,
              }}
            >
              GPS
            </div>
            <div
              style={{
                fontFamily: "Inter, Arial, sans-serif",
                fontWeight: 300,
                fontSize: 20,
                color: "#dfddd6",
                letterSpacing: "0.45em",
                marginTop: 4,
              }}
            >
              THE GPS WAY
            </div>
          </div>
        </div>

        <div
          style={{
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 300,
            fontSize: 11,
            color: "#888680",
            letterSpacing: "0.35em",
            marginTop: 24,
            textTransform: "uppercase",
          }}
        >
          Coaching School
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <DownloadButton targetRef={ref} filename="gps-way-logo-primary" size={3} />
      </div>
    </div>
  );
}

function StackedLogo() {
  const ref = useRef(null);
  return (
    <div>
      <div
        ref={ref}
        style={{
          width: 500,
          height: 500,
          background: "#1a1c1b",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Outer ring */}
        <svg
          width="320"
          height="320"
          viewBox="0 0 320 320"
          fill="none"
          style={{ position: "absolute" }}
        >
          <circle cx="160" cy="160" r="150" stroke="#d9fb06" strokeWidth="1.5" opacity="0.3" />
          <circle cx="160" cy="160" r="140" stroke="#d9fb06" strokeWidth="0.5" opacity="0.15" />
        </svg>

        <div
          style={{
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 900,
            fontSize: 100,
            color: "#d9fb06",
            letterSpacing: "0.12em",
            lineHeight: 1,
            textAlign: "center",
          }}
        >
          GPS
        </div>
        <div
          style={{
            width: 60,
            height: 2,
            background: "#d9fb06",
            margin: "16px 0",
          }}
        />
        <div
          style={{
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 300,
            fontSize: 16,
            color: "#dfddd6",
            letterSpacing: "0.5em",
            textAlign: "center",
          }}
        >
          THE GPS WAY
        </div>
        <div
          style={{
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 300,
            fontSize: 10,
            color: "#888680",
            letterSpacing: "0.4em",
            marginTop: 12,
            textTransform: "uppercase",
          }}
        >
          Method{"\u2122"}
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <DownloadButton targetRef={ref} filename="gps-way-logo-stacked" size={3} />
      </div>
    </div>
  );
}

function MinimalMark() {
  const ref = useRef(null);
  return (
    <div>
      <div
        ref={ref}
        style={{
          width: 400,
          height: 400,
          background: "#1a1c1b",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <svg width="280" height="280" viewBox="0 0 280 280" fill="none">
          {/* Outer circle */}
          <circle cx="140" cy="140" r="120" stroke="#d9fb06" strokeWidth="2.5" />
          {/* Inner circle */}
          <circle cx="140" cy="140" r="6" fill="#d9fb06" />
          {/* Cardinal lines */}
          <line x1="140" y1="28" x2="140" y2="70" stroke="#d9fb06" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="140" y1="210" x2="140" y2="252" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
          <line x1="28" y1="140" x2="70" y2="140" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
          <line x1="210" y1="140" x2="252" y2="140" stroke="#d9fb06" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
          {/* Compass needle - North */}
          <polygon points="140,30 132,140 140,134 148,140" fill="#d9fb06" opacity="0.85" />
          {/* Compass needle - South */}
          <polygon points="140,250 132,140 140,146 148,140" fill="#d9fb06" opacity="0.2" />
          {/* Tick marks */}
          <line x1="140" y1="20" x2="140" y2="28" stroke="#d9fb06" strokeWidth="1" opacity="0.5" />
          <line x1="140" y1="252" x2="140" y2="260" stroke="#d9fb06" strokeWidth="1" opacity="0.5" />
          <line x1="20" y1="140" x2="28" y2="140" stroke="#d9fb06" strokeWidth="1" opacity="0.5" />
          <line x1="252" y1="140" x2="260" y2="140" stroke="#d9fb06" strokeWidth="1" opacity="0.5" />
        </svg>
      </div>
      <div style={{ marginTop: 16 }}>
        <DownloadButton targetRef={ref} filename="gps-way-mark" size={3} />
      </div>
    </div>
  );
}

function DisplayPhoto() {
  const ref = useRef(null);
  return (
    <div>
      <div
        ref={ref}
        style={{
          width: 500,
          height: 500,
          background: "#1a1c1b",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Background circle for Instagram crop guide */}
        <svg
          width="460"
          height="460"
          viewBox="0 0 460 460"
          fill="none"
          style={{ position: "absolute" }}
        >
          <circle cx="230" cy="230" r="220" stroke="#d9fb06" strokeWidth="2" opacity="0.2" />
          <circle cx="230" cy="230" r="200" fill="rgba(217,251,6,0.03)" />
        </svg>

        <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          {/* Compass icon small */}
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            style={{ margin: "0 auto 16px" }}
          >
            <circle cx="24" cy="24" r="20" stroke="#d9fb06" strokeWidth="1.5" />
            <circle cx="24" cy="24" r="2.5" fill="#d9fb06" />
            <polygon points="24,6 21,24 24,22 27,24" fill="#d9fb06" opacity="0.8" />
            <polygon points="24,42 21,24 24,26 27,24" fill="#d9fb06" opacity="0.25" />
          </svg>

          <div
            style={{
              fontFamily: "Inter, Arial, sans-serif",
              fontWeight: 900,
              fontSize: 88,
              color: "#d9fb06",
              letterSpacing: "0.1em",
              lineHeight: 1,
            }}
          >
            GPS
          </div>
          <div
            style={{
              width: 50,
              height: 2,
              background: "#d9fb06",
              margin: "14px auto",
            }}
          />
          <div
            style={{
              fontFamily: "Inter, Arial, sans-serif",
              fontWeight: 300,
              fontSize: 15,
              color: "#dfddd6",
              letterSpacing: "0.5em",
            }}
          >
            THE GPS WAY
          </div>
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <DownloadButton targetRef={ref} filename="gps-way-display-photo" size={3} />
      </div>
    </div>
  );
}

export default function BrandLogos() {
  return (
    <div
      data-testid="brand-logos-page"
      style={{ background: "#1a1c1b", minHeight: "100vh", padding: "120px 40px 80px" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto 60px" }}>
        <div
          style={{
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 300,
            fontSize: 14,
            color: "#888680",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          Brand Assets
        </div>
        <h1
          className="brand-display"
          style={{ fontSize: "clamp(2.5rem, 4vw, 5rem)", marginBottom: 20 }}
        >
          THE GPS WAY
        </h1>
        <p style={{ color: "#888680", fontSize: 18, maxWidth: 600, lineHeight: 1.6 }}>
          Logo and display photo variations for The GPS Way. Download each as a high-res PNG.
        </p>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Primary Logo */}
        <div style={{ marginBottom: 80, paddingBottom: 80, borderBottom: "1px solid rgba(217,251,6,0.1)" }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 14, color: "#d9fb06", letterSpacing: "0.2em", marginBottom: 8 }}>
            01 — PRIMARY LOGO
          </div>
          <p style={{ color: "#888680", fontSize: 14, marginBottom: 24 }}>
            Full brand logo with text — ideal for website header, business cards, letterheads
          </p>
          <div style={{ border: "1px solid rgba(217,251,6,0.15)", display: "inline-block" }}>
            <PrimaryLogo />
          </div>
        </div>

        {/* Stacked Logo */}
        <div style={{ marginBottom: 80, paddingBottom: 80, borderBottom: "1px solid rgba(217,251,6,0.1)" }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 14, color: "#d9fb06", letterSpacing: "0.2em", marginBottom: 8 }}>
            02 — STACKED LOGO
          </div>
          <p style={{ color: "#888680", fontSize: 14, marginBottom: 24 }}>
            Compact stacked layout — ideal for Instagram profile picture, app icon, favicon
          </p>
          <div style={{ border: "1px solid rgba(217,251,6,0.15)", display: "inline-block" }}>
            <StackedLogo />
          </div>
        </div>

        {/* Minimal Mark */}
        <div style={{ marginBottom: 80, paddingBottom: 80, borderBottom: "1px solid rgba(217,251,6,0.1)" }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 14, color: "#d9fb06", letterSpacing: "0.2em", marginBottom: 8 }}>
            03 — COMPASS MARK
          </div>
          <p style={{ color: "#888680", fontSize: 14, marginBottom: 24 }}>
            Icon-only mark — ideal for watermarks, social media avatar, small spaces
          </p>
          <div style={{ border: "1px solid rgba(217,251,6,0.15)", display: "inline-block" }}>
            <MinimalMark />
          </div>
        </div>

        {/* Display Photo */}
        <div style={{ marginBottom: 80 }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 14, color: "#d9fb06", letterSpacing: "0.2em", marginBottom: 8 }}>
            04 — DISPLAY PHOTO
          </div>
          <p style={{ color: "#888680", fontSize: 14, marginBottom: 24 }}>
            Instagram / social media display photo — circular-crop optimized
          </p>
          <div style={{ border: "1px solid rgba(217,251,6,0.15)", display: "inline-block" }}>
            <DisplayPhoto />
          </div>
        </div>
      </div>
    </div>
  );
}
